import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService } from '../../config/api';
import MenuViewer from './MenuViewer';
import { Helmet } from 'react-helmet-async';
import { SparklesIcon, ClockIcon, ExclamationTriangleIcon, PhotoIcon, ListBulletIcon } from '@heroicons/react/24/outline'; // Added ListBulletIcon
import posthog from 'posthog-js';
import { BanknotesIcon } from '@heroicons/react/24/solid'

// Configuration for polling
const POLLING_INTERVAL = 60000; // 60 seconds
const MAX_RETRIES = 25; // 25 minutes max wait time

const HeaderCore = ({ name, description }) => {
    return (
        <div
            className="text-white p-6 sm:p-8 md:p-10 rounded-b-xl shadow-lg text-center bg-primary-900"
        >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-3 break-words">
                {name}
            </h1>
            {description && (
                <p
                    className="text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed break-words text-primary-100"
                >
                    {description}
                </p>
            )}
        </div>
    );
};

// New component that includes the HeaderCore and the "sticker" branding
const VendorHeaderWithSticker = ({ name, description, vendorId }) => {
    return (
        // Parent wrapper for relative positioning and bottom margin
        // Increased mb to account for the sticker height + some space below it
        <div className="relative mb-12">
            <HeaderCore name={name} description={description} />

            {/* --- Branding Sticker/Ribbon --- */}
            <div
                className="
                    absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2
                    flex items-center justify-center gap-1.5 sm:gap-2
                    px-3 py-1 sm:px-4 sm:py-1.5
                    bg-gradient-to-r bg-primary-600 via-primary-400 bg-primary-600
                    rounded-full
                    shadow-md
                    z-10 // Ensures it's visually on top if there's any overlap possibility
                "
            >
                <p className="text-xs font-medium text-primary-200 whitespace-nowrap"> {/* Adjusted text color for contrast on darker bg */}
                    Powered by
                </p>
                <img src="/logo.png" alt="Scanmaadi Logo" className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <a
                    href="https://scanmaadi.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    // Simpler text for readability on a small sticker, subtle hover
                    onClick={() => posthog.capture('public_vendor_header_scanmaadi_link_clicked', { vendorId, vendorName: name })}
                    className="text-xs sm:text-sm font-semibold text-primary-100 hover:text-white transition-colors duration-150 whitespace-nowrap"
                >
                    ScanMaadi
                </a>
            </div>
            {/* --- End Branding Sticker/Ribbon --- */}
        </div>
    );
};


// Helper component for displaying menu images
const MenuImageViewer = ({ images, vendorName }) => {
    if (!images || images.length === 0) {
        return (
            <div className="text-center py-8">
                <PhotoIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <span className="text-text-500">No menu images available for {vendorName}.</span>
            </div>
        );
    }
    // Sort images by order if 'order' property exists
    const sortedImages = [...images].sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
        <div className="space-y-6">
            {sortedImages.map((img, index) => (
                <div
                    key={img.imageURL || index}
                    className="rounded-lg shadow-md overflow-hidden bg-white border border-gray-200"
                >
                    <img
                        src={img.imageURL}
                        alt={`Menu page ${index + 1} for ${vendorName}`}
                        className="w-full h-auto object-contain"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/EEE/31343C?text=Image+Not+Found'; }}
                    />
                </div>
            ))}
        </div>
    );
};


// Helper component for the AI Processing Loader
const MenuProcessingLoader = ({ vendorName, processingPercentage }) => {
    const messages = [
        `Hold tight! ScanMaadi's AI is digitizing the menu for ${vendorName}...`, // Slightly more branding
        `Analyzing menu images for ${vendorName}...`,
        `Extracting dishes and prices for ${vendorName}...`,
        `Almost there! Finalizing the digital menu for ${vendorName}...`,
    ];
    const [message, setMessage] = useState(messages[0]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setMessage(messages[Math.floor(Math.random() * messages.length)]);
        }, 4000);
        return () => clearInterval(intervalId);
    }, []); // Stable dependency: messages array is constant

    return (
        <div
            className="flex flex-col items-center justify-center text-center p-6 sm:p-8 my-8 rounded-lg shadow-lg bg-background-100 border border-primary-200"
        >
            <div className="relative mb-6">
                <SparklesIcon className="w-20 h-20 text-accent-500" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <ClockIcon className="w-10 h-10 animate-ping opacity-50 text-primary-500" />
                </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-semibold mb-3 text-text-700">{`Digitizing ${vendorName}'s Menu`}</h2>
            <p className="mb-6 max-w-md px-2 text-text-600">{message}</p>
            <div className="w-full max-w-md rounded-full h-4 mb-2 overflow-hidden bg-gray-200">
                <div
                    className="h-4 rounded-full transition-all duration-1000 ease-out flex items-center justify-center bg-gradient-to-r from-primary-400 to-accent-500"
                    style={{
                        width: `${processingPercentage !== null ? processingPercentage : 5}%`,
                    }}
                >
                    {processingPercentage !== null && processingPercentage > 10 && (
                        <span className="text-xs font-medium px-2 text-white">
                            {processingPercentage}%
                        </span>
                    )}
                </div>
            </div>
            {processingPercentage === null && <p className="text-sm mt-1 text-text-500">Preparing AI analysis...</p>}
            <p className="text-sm mt-4 text-text-500">
                This usually takes a few minutes. You can view the menu images below while you wait.
            </p>
        </div>
    );
};

// Pay Bill Modal
const PayBillModal = ({ vendor, onClose }) => {
    const [amount, setAmount] = useState('');
    const [showPaymentLinks, setShowPaymentLinks] = useState(false);

    const handleAmountSubmit = (e) => {
        e.preventDefault();
        if (parseFloat(amount) > 0) {
            setShowPaymentLinks(true);
        }
    };

    const upiId = vendor.upiId;
    const vendorName = vendor.name.replace(/\s/g, '%20');

    // App-specific UPI URLs
    const gpayUrl = `gpay://upi/pay?pa=${upiId}&pn=${vendorName}&am=${amount}&cu=INR`;
    const phonepeUrl = `phonepe://pay?pa=${upiId}&pn=${vendorName}&am=${amount}&cu=INR`;
    const paytmUrl = `paytmmp://pay?pa=${upiId}&pn=${vendorName}&am=${amount}&cu=INR`;
    const genericUpiUrl = `upi://pay?pa=${upiId}&pn=${vendorName}&am=${amount}&cu=INR`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl transform transition-all" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Pay Bill</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="text-center mb-4">
                    <p className="text-gray-600">Paying to</p>
                    <p className="text-lg font-semibold text-gray-900">{vendor.name}</p>
                </div>
                {!showPaymentLinks ? (
                    <form onSubmit={handleAmountSubmit}>
                        <label htmlFor="billAmount" className="block text-sm font-medium text-gray-700">Enter Bill Amount (INR)</label>
                        <input
                            type="number"
                            id="billAmount"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            placeholder="e.g., 500"
                            min="1"
                            required
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-primary-500 text-white rounded-md">Proceed to Pay</button>
                        </div>
                    </form>
                ) : (
                    <div>
                        <div className="text-center mb-6">
                            <p className="text-3xl font-bold text-gray-900">₹{amount}</p>
                            <p className="text-gray-600">Amount to pay</p>
                        </div>
                        <div className="space-y-3">
                            <a href={gpayUrl} className="flex items-center justify-center w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-md">
                                <img src="https://upload.wikimedia.org/wikipedia/commons/c/c7/Google_Pay_Logo.svg" alt="GPay" className="w-6 h-6 mr-2" />
                                Pay with Google Pay
                            </a>
                            <a href={phonepeUrl} className="flex items-center justify-center w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-3 px-4 rounded-xl font-medium hover:from-purple-700 hover:to-purple-800 transition-all shadow-md">
                                <img src="https://upload.wikimedia.org/wikipedia/commons/7/71/PhonePe_Logo.svg" alt="PhonePe" className="w-6 h-6 mr-2 bg-white rounded p-0.5" />
                                Pay with PhonePe
                            </a>
                            <a href={paytmUrl} className="flex items-center justify-center w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 px-4 rounded-xl font-medium hover:from-blue-900 hover:to-blue-950 transition-all shadow-md">
                                <span className="font-bold mr-2">₹</span>
                                Pay with Paytm
                            </a>
                            <a href={genericUpiUrl} className="flex items-center justify-center w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white py-3 px-4 rounded-xl font-medium hover:from-gray-700 hover:to-gray-800 transition-all shadow-md">
                                Other UPI Apps
                            </a>                        </div>
                        <button
                            onClick={() => {
                                setShowPaymentLinks(false);
                                setAmount('');
                            }}
                            className="mt-4 w-full text-center py-2 text-gray-600"
                        >
                            Change Amount
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};


// Main VendorView Component
function VendorView() {
    const { vendorId } = useParams();
    const [vendor, setVendor] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [menuStatus, setMenuStatus] = useState('PENDING_VENDOR');
    const [error, setError] = useState(null);
    const pollingTimeoutRef = useRef(null);
    const retryCountRef = useRef(0);
    const [processingPercentage, setProcessingPercentage] = useState(null);
    const [isPayModalOpen, setIsPayModalOpen] = useState(false);

    const [showImagesInstead, setShowImagesInstead] = useState(false);

    const pageTitle = isLoading ? 'Loading Menu...' : vendor ? `${vendor.name} | Digital Menu by ScanMaadi` : 'Menu Not Found | ScanMaadi';
    const metaDescription = isLoading ? 'Loading restaurant menu details.' :
        vendor ? `Browse the digital menu of ${vendor.name}. ${vendor.description || ''} Searchable, filterable, and always up-to-date. Powered by ScanMaadi.` :
            'The requested restaurant menu could not be found.';

    const clearPolling = useCallback(() => {
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        posthog.capture('public_menu_back_to_top_clicked', { vendorId });
    };

    const fetchMenuData = useCallback(async (currentVendorId) => {
        if (!currentVendorId) {
            setError("Cannot fetch menu without a vendor ID.");
            setMenuStatus("MENU_ERROR");
            posthog.capture('public_vendor_error_fetch_menu_no_id', { forVendorId: currentVendorId });

            return;
        }
        clearPolling();
        posthog.capture('public_vendor_menu_data_fetch_attempt', { vendorId: currentVendorId, retryCount: retryCountRef.current });

        try {
            const response = await apiService.menus.getLatestMenu(currentVendorId);
            const menuAPIResponse = response.data.data;

            if (menuAPIResponse && menuAPIResponse.status) {
                const currentStatus = menuAPIResponse.status;
                posthog.capture('public_vendor_menu_status_received', { vendorId: currentVendorId, status: currentStatus, retryCount: retryCountRef.current });

                if (currentStatus.startsWith('PROCESSING')) {
                    setMenuStatus('PROCESSING');
                    const percentageMatch = currentStatus.match(/PROCESSING\s*(\d+)%/);
                    setProcessingPercentage(percentageMatch ? parseInt(percentageMatch[1], 10) : (retryCountRef.current > 0 ? 10 : 0));
                    setShowImagesInstead(false); // Reset view preference
                    const currentPercentage = percentageMatch ? parseInt(percentageMatch[1], 10) : 0;
                    posthog.capture('public_vendor_menu_status_processing', { vendorId: currentVendorId, percentage: currentPercentage, retryCount: retryCountRef.current });


                    if (retryCountRef.current < MAX_RETRIES) {
                        retryCountRef.current += 1;
                        pollingTimeoutRef.current = setTimeout(() => fetchMenuData(currentVendorId), POLLING_INTERVAL);
                    } else {
                        setError('The menu is taking longer than usual to process. You can view the images for now, or try refreshing later.');
                        setMenuStatus('IMAGES_ONLY');
                        posthog.capture('public_vendor_menu_processing_timeout', { vendorId: currentVendorId, retries: retryCountRef.current });

                    }
                } else if (currentStatus === 'PROCESSED' || currentStatus === 'COMPLETED') {
                    setMenuStatus('PROCESSED');
                    setProcessingPercentage(100);
                    setShowImagesInstead(false); // Default to showing processed menu
                    posthog.capture('public_vendor_menu_status_processed_completed', { vendorId: currentVendorId, status: currentStatus });

                } else {
                    setError(`There was an issue preparing the menu (Status: ${currentStatus}). Please contact support or view images.`);
                    setMenuStatus('IMAGES_ONLY');
                    setShowImagesInstead(true); // Force image view on error
                    posthog.capture('public_vendor_menu_status_error_api', { vendorId: currentVendorId, status: currentStatus, error: `There was an issue preparing the menu (Status: ${currentStatus}).` });

                }
            } else { // If no status, assume it's still queuing or initial state
                setMenuStatus('PROCESSING');
                setProcessingPercentage(0); // Start with 0
                setShowImagesInstead(false); // Reset view preference
                posthog.capture('public_vendor_menu_status_no_status_in_response', { vendorId: currentVendorId, retryCount: retryCountRef.current });

                if (retryCountRef.current < MAX_RETRIES) {
                    retryCountRef.current += 1;
                    pollingTimeoutRef.current = setTimeout(() => fetchMenuData(currentVendorId), POLLING_INTERVAL);
                } else {
                    setError('The menu is not yet available. You can view the images for now, or try refreshing later.');
                    setMenuStatus('IMAGES_ONLY');
                    posthog.capture('public_vendor_menu_initial_load_timeout', { vendorId: currentVendorId, retries: retryCountRef.current });

                }
            }
        } catch (err) {
            setShowImagesInstead(true); // Show images on fetch error if possible
            const errorMessage = err.response?.data?.message || err.message;
            posthog.capture('public_vendor_menu_data_fetch_error', { vendorId: currentVendorId, error: errorMessage, status: err.response?.status, retryCount: retryCountRef.current });

            if (err.response && err.response.status === 404) {
                // Menu not found, might still be in initial processing stages
                setMenuStatus('PROCESSING');
                setProcessingPercentage(0);
                if (retryCountRef.current < MAX_RETRIES) {
                    retryCountRef.current += 1;
                    pollingTimeoutRef.current = setTimeout(() => fetchMenuData(currentVendorId), POLLING_INTERVAL);
                } else {
                    setError('Menu not found after several attempts. Please check back later or view available images.');
                    setMenuStatus('IMAGES_ONLY');
                    posthog.capture('public_vendor_menu_not_found_after_retries', { vendorId: currentVendorId, retries: retryCountRef.current });

                }
            } else {
                console.error("Error fetching menu data:", err);
                setError('Failed to load menu information. Please try again or view available images.');
                setMenuStatus('IMAGES_ONLY');
            }
        }
    }, [clearPolling]);

    useEffect(() => {
        posthog.capture('public_vendor_page_viewed', { vendorId });

        const fetchVendorDetails = async () => {
            if (!vendorId) {
                setError('Vendor ID is missing.');
                setIsLoading(false);
                setMenuStatus('MENU_ERROR');
                posthog.capture('public_vendor_error_missing_id_param');

                return;
            }
            setIsLoading(true);
            setError(null);
            retryCountRef.current = 0;
            setProcessingPercentage(null);
            setShowImagesInstead(false); // Reset on new vendor load
            clearPolling();
            posthog.capture('public_vendor_details_fetch_attempt', { vendorId });


            try {
                const response = await apiService.vendors.get(vendorId);
                setVendor(response.data.data);
                const vendorData = response.data.data;

                if (!vendorData.confirmed) {
                    setError("This restaurant's menu is not yet available to the public.");
                    setMenuStatus('MENU_ERROR');
                    setShowImagesInstead(false); // Show images if available
                    setIsLoading(false);
                    return;
                }

                setMenuStatus('VENDOR_LOADED');
                posthog.capture('public_vendor_details_load_success', { vendorId, vendorName: vendorData.name });
                if (vendorData.isOffline) {
                    setError('This restaurant is currently offline. Please check back later.');
                    setMenuStatus('MENU_ERROR');
                    setShowImagesInstead(false); // Show images if available
                    setIsLoading(false);
                    posthog.capture('public_vendor_details_load_offline', { vendorId, vendorName: vendorData.name });
                    return;
                }
                if (!vendorData.disableAiProcessing) {
                    // Only fetch menu data if AI processing is enabled
                    fetchMenuData(vendorId);
                } else {
                    setShowImagesInstead(true); // Show images if AI processing is disabled 
                    setMenuStatus('IMAGES_ONLY'); // Set status to images only 
                }
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    setError('Sorry, the restaurant you are looking for could not be found.');
                    posthog.capture('public_vendor_details_load_failed_not_found', { vendorId, error: errorMessage });

                } else {
                    console.error("Error fetching vendor details:", err);
                    setError('Failed to load restaurant details. Please check your connection and try again.');
                    posthog.capture('public_vendor_details_load_failed_error', { vendorId, error: errorMessage, status: err.response?.status });

                }
                setMenuStatus('MENU_ERROR');
            } finally {
                setIsLoading(false);
            }
        };

        fetchVendorDetails();
        return () => clearPolling(); // Cleanup polling
    }, [vendorId, fetchMenuData, clearPolling]);

    if (isPayModalOpen) {
        return <PayBillModal vendor={vendor} onClose={() => setIsPayModalOpen(false)} />
    }
    // Loading State (Unchanged)
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-background-100">
                <div
                    className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 border-t-transparent"
                ></div>
                <p className="ml-3 mt-4 text-text-600">Loading restaurant details...</p>
            </div>
        );
    }

    // Vendor Not Found
    if (!vendor) {
        return (
            <>
                <Helmet>
                    <title>{error ? 'Error' : 'Restaurant Not Found'} - ScanMaadi</title>
                    <meta name="description" content={error || "The requested restaurant menu could not be found."} />
                </Helmet>
                <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center bg-background-100">
                    <ExclamationTriangleIcon className="w-16 h-16 mb-4 text-error-500" />
                    <h1 className="text-2xl font-bold mb-2 text-text-700">{error ? "Oops! Something went wrong." : "Restaurant Not Found"}</h1>
                    <p className="mb-6 max-w-md text-text-500">{error || "The restaurant you're looking for does not exist or the link may be incorrect."}</p>
                    {error ? (
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-2 rounded-lg transition-colors shadow-md bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        >
                            Try Again
                        </button>
                    ) : (
                        <Link
                            to="/"
                            className="px-6 py-2 rounded-lg transition-colors shadow-md bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        >
                            Go to Homepage
                        </Link>
                    )}
                    {error && <p className="mt-8 text-sm text-text-400">If the problem persists, please contact support.</p>}
                </div>
            </>
        );
    }

    // Combined className for action buttons (Unchanged)
    const actionButtonClassName = "px-6 py-2 rounded-lg transition-colors shadow-md bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2";

    // Button to toggle between processed menu and images
    const ToggleViewButton = () => (
        // Only show toggle if menu is processed AND there are images to show
        vendor?.menuImageInfo?.length > 0 && (
            <div className="text-center mb-6">
                <button
                    onClick={() => {
                        setShowImagesInstead(!showImagesInstead);
                        posthog.capture('public_vendor_toggle_menu_view_clicked', { vendorId, vendorName: vendor.name, showingImagesAfterToggle: !showImagesInstead });
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-text-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    {showImagesInstead ? (
                        <>
                            <ListBulletIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                            Show Digital Menu
                        </>
                    ) : (
                        <>
                            <PhotoIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                            Show Menu Images
                        </>
                    )}
                </button>
            </div>
        )
    );


    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={metaDescription} />
                {vendor && <meta property="og:title" content={`${vendor.name} - Digital Menu | ScanMaadi`} />}
                {vendor && <meta property="og:description" content={metaDescription} />}
                {vendor && vendor.menuImageInfo && vendor.menuImageInfo.length > 0 &&
                    <meta property="og:image" content={vendor.menuImageInfo[0].imageURL} />
                }
            </Helmet>
            <div className="min-h-screen text-text-700 bg-background-100 flex flex-col">
                {/* Floating Action Buttons Container */}
                <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3" style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999 }}>
                    {/* Pay Bill Button */}
                    {/* {vendor?.upiId && !isPayModalOpen && (
                        <button
                            onClick={() => {
                                setIsPayModalOpen(true);
                                posthog.capture('public_vendor_pay_bill_clicked', { vendorId: vendor.id, vendorName: vendor.name, source: 'floating_button' });
                            }}
                            className="group flex items-center bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                            style={{ backgroundColor: '#10b981' }}
                        >
                            <BanknotesIcon className="h-5 w-5 mr-2" />
                            <span className="font-semibold">Pay Bill</span>
                        </button>
                    )} */}
                    {/* Back to Top Button */}
                    <button
                        id="back-to-top-btn"
                        onClick={scrollToTop}
                        className="fixed bottom-20 right-6 p-3 rounded-full shadow-lg text-white bg-primary-500 hover:bg-primary-600 transition-all duration-300 opacity-0 pointer-events-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        aria-label="Back to top"
                    >
                        <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" /></svg>
                    </button>
                </div>
                <div className="container mx-auto px-0 sm:px-4 py-0 max-w-4xl flex-1">
                    <VendorHeaderWithSticker name={vendor.name} description={vendor.description} />

                    {/* Pay Bill Banner */}
                    {vendor?.upiId && (
                        <div className="mx-4 sm:mx-0 mt-4 mb-6">
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                                <div className="flex items-center">
                                    <BanknotesIcon className="h-8 w-8 text-green-600 mr-3" />
                                    <div>
                                        <p className="text-sm text-gray-600">Quick & Secure Payment</p>
                                        <p className="font-semibold text-gray-900">Pay your bill with UPI</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setIsPayModalOpen(true);
                                        posthog.capture('public_vendor_pay_bill_clicked', { vendorId: vendor.id, vendorName: vendor.name, source: 'header_banner' });
                                    }}
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                    Pay Now
                                </button>
                            </div>
                        </div>
                    )}

                    <main className="p-4 sm:p-0">

                        {/* --- Display Logic based on menuStatus --- */}

                        {menuStatus === 'PROCESSING' && (
                            <>
                                <MenuProcessingLoader vendorName={vendor.name} processingPercentage={processingPercentage} />
                                {/* Show images below loader if available */}
                                {vendor.menuImageInfo && vendor.menuImageInfo.length > 0 && (
                                    <div className="mt-8">
                                        <MenuImageViewer images={vendor.menuImageInfo} vendorName={vendor.name} />
                                    </div>
                                )}
                            </>
                        )}

                        {menuStatus === 'PROCESSED' && vendorId && (
                            <>
                                {/* *** ADDED: Toggle Button *** */}
                                <ToggleViewButton />

                                {/* *** UPDATED: Conditionally show MenuViewer or MenuImageViewer *** */}
                                {showImagesInstead ? (
                                    vendor.menuImageInfo && vendor.menuImageInfo.length > 0 ? (
                                        <MenuImageViewer images={vendor.menuImageInfo} vendorName={vendor.name} />
                                    ) : (
                                        <div className="text-center py-8 text-text-500">No menu images found to display.</div>
                                    )

                                ) : (
                                    <MenuViewer vendorId={vendorId} />
                                )}
                            </>
                        )}

                        {menuStatus === 'IMAGES_ONLY' && (
                            <>
                                {error &&
                                    <div className="mb-6 p-4 border-l-4 rounded-md bg-yellow-50 border-yellow-400" role="alert"> {/* Mapped warning colors */}
                                        <p className="font-bold text-yellow-800">Note:</p> {/* Darker text */}
                                        <p className="text-yellow-700">{error}</p>
                                    </div>
                                }
                                {/* Always show images in this state if available */}
                                <h2 className="text-2xl font-semibold text-center mb-4 text-text-700">Menu Images</h2>
                                <MenuImageViewer images={vendor.menuImageInfo} vendorName={vendor.name} />
                            </>
                        )}

                        {menuStatus === 'MENU_ERROR' && error && vendor && (
                            <div
                                className="flex flex-col items-center justify-center text-center p-8 min-h-[300px] rounded-lg shadow-md bg-white border border-error-300" /* Mapped background-paper, lighter error border */
                            >
                                <ExclamationTriangleIcon className="w-16 h-16 mb-4 text-error-500" />
                                <h2 className="text-2xl font-semibold mb-3 text-text-700">Menu Temporarily Unavailable</h2>
                                <p className="mb-6 max-w-md text-text-500">{error}</p>
                                <button
                                    onClick={() => {
                                        posthog.capture('public_vendor_retry_menu_load_clicked', { vendorId, vendorName: vendor.name, currentError: error });

                                        window.location.reload();
                                    }}
                                    className={actionButtonClassName}
                                >
                                    Retry Loading Menu
                                </button>
                                {/* Show images below error message if available */}
                                {showImagesInstead && vendor.menuImageInfo && vendor.menuImageInfo.length > 0 && (
                                    <div className="mt-8 w-full">
                                        <p className="mb-4 text-text-600">In the meantime, you can view the uploaded menu images:</p>
                                        <MenuImageViewer images={vendor.menuImageInfo} vendorName={vendor.name} />
                                    </div>
                                )}
                            </div>
                        )}
                    </main>
                </div>
                {/* *** UPDATED FOOTER *** */}
                <footer
                    className="text-center py-2 mt-5 border-t border-gray-200 bg-white" /* Mapped border and background-paper */
                >
                    <div className="flex items-center justify-center space-x-2">

                        <p className="text-sm text-text-400">
                            Powered by
                        </p>
                        <img src="/logo.png" alt="Scanmaadi Logo" className="h-8 w-8 mr-3" />
                        <a href="https://scanmaadi.com" target="_blank"
                            onClick={() => posthog.capture('public_vendor_footer_scanmaadi_link_clicked', { vendorId: vendor?.id, vendorName: vendor?.name })}

                            className="text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-highlight-400 relative">
                            ScanMaadi
                        </a>
                    </div>
                </footer>
            </div>
        </>
    );
}

export default VendorView;